import React, { useState, useCallback, useRef } from 'react';
import styled from 'styled-components';
import apiService, {
  BulkSearchMatch,
  BulkSearchAmbiguous,
  BulkSearchNotFound,
  BulkSearchCandidate,
  BulkRateItem,
  BulkRateResponse,
} from '../services/api';

// ── Layout ──────────────────────────────────────────────────────────────────

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const Modal = styled.div`
  background: var(--color-surface);
  border-radius: 14px;
  padding: 2rem;
  width: 100%;
  max-width: 680px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.4);
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const ModalTitle = styled.h2`
  margin: 0;
  color: var(--color-text);
  font-size: 1.4rem;
`;

const ModalSubtitle = styled.p`
  margin: -0.75rem 0 0;
  color: var(--color-text-secondary);
  font-size: 0.9rem;
`;

// ── Shared primitives ────────────────────────────────────────────────────────

const Row = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: center;
`;

const Spacer = styled.div`flex: 1;`;

const Btn = styled.button<{ $variant?: 'primary' | 'danger' | 'ghost' }>`
  padding: 0.55rem 1.1rem;
  border-radius: 7px;
  border: none;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
  white-space: nowrap;

  ${({ $variant }) =>
    $variant === 'primary'
      ? `background: var(--color-accent); color: white;
         &:hover { background: var(--color-accent-hover); }
         &:disabled { opacity: 0.5; cursor: not-allowed; }`
      : $variant === 'danger'
      ? `background: #e53e3e; color: white;
         &:hover { background: #c53030; }`
      : `background: var(--color-bg); color: var(--color-text);
         border: 1px solid var(--color-border);
         &:hover { background: var(--color-border); }
         &:disabled { opacity: 0.5; cursor: not-allowed; }`}
`;

const SmallBtn = styled(Btn)`
  padding: 0.35rem 0.75rem;
  font-size: 0.82rem;
`;

const ErrorMsg = styled.div`
  background: #fed7d7;
  color: #c53030;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  font-size: 0.9rem;
`;

const Spinner = styled.div`
  text-align: center;
  padding: 2.5rem 0;
  color: var(--color-text-secondary);
  font-size: 1rem;
`;

// ── Upload step ──────────────────────────────────────────────────────────────

const DropZone = styled.div<{ $active: boolean }>`
  border: 2px dashed ${({ $active }) => ($active ? 'var(--color-accent)' : 'var(--color-border)')};
  border-radius: 10px;
  padding: 2.5rem 1.5rem;
  text-align: center;
  cursor: pointer;
  background: ${({ $active }) => ($active ? 'var(--color-surface-subtle, rgba(0,0,0,0.04))' : 'transparent')};
  transition: border-color 0.2s, background 0.2s;
  color: var(--color-text-secondary);
`;

// ── Review step ──────────────────────────────────────────────────────────────

const SummaryBar = styled.div`
  display: flex;
  gap: 1.5rem;
  flex-wrap: wrap;
  padding: 0.75rem 1rem;
  background: var(--color-bg);
  border-radius: 8px;
  font-size: 0.9rem;
`;

const SummaryChip = styled.span<{ $color: 'green' | 'orange' | 'red' }>`
  font-weight: 600;
  color: ${({ $color }) =>
    $color === 'green' ? '#38a169' : $color === 'orange' ? '#dd6b20' : '#e53e3e'};
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 1rem;
  color: var(--color-text);
  border-bottom: 1px solid var(--color-border);
  padding-bottom: 0.5rem;
`;

// Matched list
const MatchedItem = styled.label`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 0;
  cursor: pointer;
  color: var(--color-text);
  font-size: 0.9rem;
`;

const MatchedMeta = styled.span`
  color: var(--color-text-secondary);
  font-size: 0.82rem;
`;

// Ambiguous queue
const AmbiguousCard = styled.div<{ $unresolved: boolean }>`
  border: 1px solid ${({ $unresolved }) => ($unresolved ? '#dd6b20' : 'var(--color-border)')};
  border-radius: 10px;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const AmbiguousQuery = styled.div`
  font-size: 0.92rem;
  color: var(--color-text);
`;

const CandidateGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 0.5rem;
`;

const CandidateCard = styled.button<{ $selected: boolean }>`
  background: ${({ $selected }) => ($selected ? 'var(--color-accent)' : 'var(--color-bg)')};
  color: ${({ $selected }) => ($selected ? 'white' : 'var(--color-text)')};
  border: 2px solid ${({ $selected }) => ($selected ? 'var(--color-accent)' : 'var(--color-border)')};
  border-radius: 8px;
  padding: 0.6rem 0.75rem;
  cursor: pointer;
  text-align: left;
  font-size: 0.82rem;
  line-height: 1.4;
  transition: all 0.15s;

  &:hover {
    border-color: var(--color-accent);
  }

  strong {
    display: block;
    font-size: 0.85rem;
  }

  span {
    color: ${({ $selected }) => ($selected ? 'rgba(255,255,255,0.8)' : 'var(--color-text-secondary)')};
    font-size: 0.78rem;
  }
`;

const SkipLink = styled.button`
  background: none;
  border: none;
  color: var(--color-text-secondary);
  cursor: pointer;
  font-size: 0.82rem;
  padding: 0;
  text-decoration: underline;
  align-self: flex-start;

  &:hover { color: var(--color-text); }
`;

// Not-found list
const NotFoundItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.5rem 0;
  border-bottom: 1px solid var(--color-border);

  &:last-child { border-bottom: none; }
`;

const ReSearchRow = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const TitleInput = styled.input`
  flex: 1;
  padding: 0.45rem 0.7rem;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  background: var(--color-bg);
  color: var(--color-text);
  font-size: 0.88rem;

  &:focus {
    outline: none;
    border-color: var(--color-accent);
  }
`;

const ReSearchResults = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

const ReSearchResultItem = styled.button`
  background: var(--color-bg);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  padding: 0.45rem 0.7rem;
  text-align: left;
  cursor: pointer;
  font-size: 0.85rem;
  color: var(--color-text);
  transition: background 0.15s;

  &:hover {
    background: var(--color-border);
  }

  span {
    color: var(--color-text-secondary);
    font-size: 0.78rem;
    margin-left: 0.4rem;
  }
`;

// Done step
const DoneBox = styled.div`
  text-align: center;
  padding: 1rem 0;
`;

const DoneStat = styled.div`
  font-size: 2rem;
  font-weight: bold;
  color: var(--color-accent);
`;

const DoneLabel = styled.div`
  font-size: 0.9rem;
  color: var(--color-text-secondary);
  margin-bottom: 1rem;
`;

// ── Helpers ──────────────────────────────────────────────────────────────────

function releaseYear(date?: string): string {
  if (!date) return '';
  return date.slice(0, 4);
}

function parseCsvLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') { current += '"'; i++; }
      else { inQuotes = !inQuotes; }
    } else if (ch === ',' && !inQuotes) {
      result.push(current.trim());
      current = '';
    } else {
      current += ch;
    }
  }
  result.push(current.trim());
  return result;
}

function parseCsv(text: string): { items: { title: string; rating: number }[]; error?: string } {
  const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
  if (!lines.length) return { items: [], error: 'File is empty.' };

  const startIdx = lines[0].toLowerCase().replace(/\s/g, '').startsWith('title') ? 1 : 0;
  const dataLines = lines.slice(startIdx);
  if (!dataLines.length) return { items: [], error: 'No data rows found after header.' };

  const items: { title: string; rating: number }[] = [];
  const errors: string[] = [];

  dataLines.forEach((line, idx) => {
    const parts = parseCsvLine(line);
    const title = parts[0];
    const ratingStr = parts[1];
    const rating = parseInt(ratingStr, 10);

    if (!title) { errors.push(`Row ${startIdx + idx + 1}: missing title`); return; }
    if (!ratingStr || isNaN(rating) || rating < 1 || rating > 5) {
      errors.push(`Row ${startIdx + idx + 1}: rating must be 1–5 (got "${ratingStr}")`);
      return;
    }
    items.push({ title, rating });
  });

  if (errors.length) return { items, error: errors.slice(0, 3).join(' | ') + (errors.length > 3 ? ` …and ${errors.length - 3} more` : '') };
  return { items };
}

// ── Component ────────────────────────────────────────────────────────────────

type Step = 'upload' | 'processing' | 'review' | 'saving' | 'done';

interface ReSearchState {
  query: string;
  loading: boolean;
  candidates: BulkSearchCandidate[] | null;
  noResult: boolean;
}

interface Props {
  onClose: () => void;
  onImportComplete: () => void;
}

const BulkImportModal: React.FC<Props> = ({ onClose, onImportComplete }) => {
  const [step, setStep] = useState<Step>('upload');
  const [dragOver, setDragOver] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [parseError, setParseError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Review state
  const [matched, setMatched] = useState<BulkSearchMatch[]>([]);
  const [ambiguous, setAmbiguous] = useState<BulkSearchAmbiguous[]>([]);
  const [notFound, setNotFound] = useState<BulkSearchNotFound[]>([]);

  const [excluded, setExcluded] = useState<Set<number>>(new Set());
  const [resolvedAmbiguous, setResolvedAmbiguous] = useState<Map<number, BulkSearchCandidate>>(new Map());
  const [skippedAmbiguous, setSkippedAmbiguous] = useState<Set<number>>(new Set());
  const [reSearchState, setReSearchState] = useState<Map<number, ReSearchState>>(new Map());

  // Done state
  const [doneResult, setDoneResult] = useState<BulkRateResponse | null>(null);
  const [saveError, setSaveError] = useState('');

  // ── Upload step handlers ───────────────────────────────────────────────────

  const handleFile = useCallback((f: File) => {
    setParseError('');
    setFile(f);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  }, [handleFile]);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) handleFile(f);
  }, [handleFile]);

  const handleProcess = useCallback(async () => {
    if (!file) return;
    const text = await file.text();
    const { items, error } = parseCsv(text);

    if (!items.length) {
      setParseError(error || 'No valid rows found in file.');
      return;
    }
    if (error) setParseError(error);

    setStep('processing');
    try {
      const result = await apiService.bulkSearch(items);
      setMatched(result.matched);
      setAmbiguous(result.ambiguous);
      setNotFound(result.not_found);
      setExcluded(new Set());
      setResolvedAmbiguous(new Map());
      setSkippedAmbiguous(new Set());
      setReSearchState(new Map());
      setStep('review');
    } catch (err: any) {
      setParseError(err.response?.data?.detail || 'Failed to search movies. Please try again.');
      setStep('upload');
    }
  }, [file]);

  // ── Review step handlers ───────────────────────────────────────────────────

  const toggleExclude = useCallback((rowIndex: number) => {
    setExcluded(prev => {
      const next = new Set(prev);
      if (next.has(rowIndex)) next.delete(rowIndex);
      else next.add(rowIndex);
      return next;
    });
  }, []);

  const resolveAmbiguous = useCallback((rowIndex: number, candidate: BulkSearchCandidate) => {
    setResolvedAmbiguous(prev => new Map(prev).set(rowIndex, candidate));
    setSkippedAmbiguous(prev => { const n = new Set(prev); n.delete(rowIndex); return n; });
  }, []);

  const skipAmbiguous = useCallback((rowIndex: number) => {
    setSkippedAmbiguous(prev => new Set(prev).add(rowIndex));
    setResolvedAmbiguous(prev => { const n = new Map(prev); n.delete(rowIndex); return n; });
  }, []);

  const handleReSearchQueryChange = useCallback((rowIndex: number, query: string) => {
    setReSearchState(prev => {
      const next = new Map(prev);
      const cur = next.get(rowIndex) ?? { query: '', loading: false, candidates: null, noResult: false };
      next.set(rowIndex, { ...cur, query, candidates: null, noResult: false });
      return next;
    });
  }, []);

  const handleReSearch = useCallback(async (item: BulkSearchNotFound) => {
    const state = reSearchState.get(item.row_index);
    const query = state?.query ?? item.original_title;
    if (!query.trim()) return;

    setReSearchState(prev => {
      const next = new Map(prev);
      next.set(item.row_index, { query, loading: true, candidates: null, noResult: false });
      return next;
    });

    try {
      const result = await apiService.bulkSearch([{ title: query, rating: item.rating }]);
      const m = result.matched[0];
      const amb = result.ambiguous[0];

      if (m) {
        // Promote to matched list
        setMatched(prev => [...prev, { ...m, row_index: item.row_index, original_title: item.original_title, rating: item.rating }]);
        setNotFound(prev => prev.filter(nf => nf.row_index !== item.row_index));
        setReSearchState(prev => { const n = new Map(prev); n.delete(item.row_index); return n; });
      } else if (amb) {
        setReSearchState(prev => new Map(prev).set(item.row_index, {
          query, loading: false, candidates: amb.candidates, noResult: false,
        }));
      } else {
        setReSearchState(prev => new Map(prev).set(item.row_index, {
          query, loading: false, candidates: null, noResult: true,
        }));
      }
    } catch {
      setReSearchState(prev => new Map(prev).set(item.row_index, {
        query, loading: false, candidates: null, noResult: true,
      }));
    }
  }, [reSearchState]);

  const selectReSearchResult = useCallback((item: BulkSearchNotFound, candidate: BulkSearchCandidate) => {
    setMatched(prev => [
      ...prev,
      {
        row_index: item.row_index,
        original_title: item.original_title,
        rating: item.rating,
        tmdb_id: candidate.tmdb_id,
        title: candidate.title,
        release_date: candidate.release_date,
        poster_path: candidate.poster_path,
        vote_average: candidate.vote_average,
      },
    ]);
    setNotFound(prev => prev.filter(nf => nf.row_index !== item.row_index));
    setReSearchState(prev => { const n = new Map(prev); n.delete(item.row_index); return n; });
  }, []);

  // ── Save ──────────────────────────────────────────────────────────────────

  const unresolvedCount = ambiguous.filter(
    a => !resolvedAmbiguous.has(a.row_index) && !skippedAmbiguous.has(a.row_index)
  ).length;

  const importItems: BulkRateItem[] = [
    ...matched
      .filter(m => !excluded.has(m.row_index))
      .map(m => ({ tmdb_id: m.tmdb_id, title: m.title, user_rating: m.rating, release_date: m.release_date })),
    ...Array.from(resolvedAmbiguous.entries()).map(([rowIndex, c]) => {
      const amb = ambiguous.find(a => a.row_index === rowIndex)!;
      return { tmdb_id: c.tmdb_id, title: c.title, user_rating: amb.rating, release_date: c.release_date };
    }),
  ];

  const handleSave = useCallback(async () => {
    if (!importItems.length) return;
    setSaveError('');
    setStep('saving');
    try {
      const result = await apiService.bulkRate(importItems);
      setDoneResult(result);
      setStep('done');
    } catch (err: any) {
      setSaveError(err.response?.data?.detail || 'Failed to save ratings. Please try again.');
      setStep('review');
    }
  }, [importItems]);

  const handleDone = useCallback(() => {
    onImportComplete();
    onClose();
  }, [onImportComplete, onClose]);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Overlay onClick={() => step === 'upload' && onClose()}>
      <Modal onClick={e => e.stopPropagation()}>

        {/* ── Upload ── */}
        {step === 'upload' && (
          <>
            <div>
              <ModalTitle>Import Ratings from CSV</ModalTitle>
              <ModalSubtitle>Download the template, fill in movie titles and star ratings (1–5), then upload.</ModalSubtitle>
            </div>

            <Btn onClick={() => apiService.downloadCsvTemplate()}>
              Download Template
            </Btn>

            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              style={{ display: 'none' }}
              onChange={handleFileInput}
            />
            <DropZone
              $active={dragOver}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
            >
              {file ? (
                <span style={{ color: 'var(--color-text)' }}>{file.name}</span>
              ) : (
                <span>Drop your CSV here, or click to browse</span>
              )}
            </DropZone>

            {parseError && <ErrorMsg>{parseError}</ErrorMsg>}

            <Row>
              <Btn onClick={onClose}>Cancel</Btn>
              <Spacer />
              <Btn $variant="primary" onClick={handleProcess} disabled={!file}>
                Process File
              </Btn>
            </Row>
          </>
        )}

        {/* ── Processing ── */}
        {step === 'processing' && (
          <Spinner>Searching for movies… this may take a moment.</Spinner>
        )}

        {/* ── Review ── */}
        {step === 'review' && (
          <>
            <div>
              <ModalTitle>Review Results</ModalTitle>
            </div>

            <SummaryBar>
              <span><SummaryChip $color="green">{matched.length}</SummaryChip> ready to import</span>
              {ambiguous.length > 0 && (
                <span><SummaryChip $color="orange">{unresolvedCount} of {ambiguous.length}</SummaryChip> need your input</span>
              )}
              {notFound.length > 0 && (
                <span><SummaryChip $color="red">{notFound.length}</SummaryChip> not found</span>
              )}
            </SummaryBar>

            {saveError && <ErrorMsg>{saveError}</ErrorMsg>}

            {/* Matched */}
            {matched.length > 0 && (
              <Section>
                <SectionTitle>Ready to import ({matched.filter(m => !excluded.has(m.row_index)).length} selected)</SectionTitle>
                {matched.map(m => (
                  <MatchedItem key={m.row_index}>
                    <input
                      type="checkbox"
                      checked={!excluded.has(m.row_index)}
                      onChange={() => toggleExclude(m.row_index)}
                    />
                    <span>
                      {m.title}
                      {m.release_date && <MatchedMeta> ({releaseYear(m.release_date)})</MatchedMeta>}
                      <MatchedMeta> — {'★'.repeat(m.rating)}</MatchedMeta>
                    </span>
                  </MatchedItem>
                ))}
              </Section>
            )}

            {/* Ambiguous */}
            {ambiguous.length > 0 && (
              <Section>
                <SectionTitle>
                  Needs your input — pick the right movie for each
                  {unresolvedCount > 0 && <span style={{ color: '#dd6b20', fontWeight: 'normal', fontSize: '0.85rem' }}> ({unresolvedCount} unresolved — will be skipped)</span>}
                </SectionTitle>
                {ambiguous.map(item => {
                  const resolved = resolvedAmbiguous.get(item.row_index);
                  const skipped = skippedAmbiguous.has(item.row_index);
                  return (
                    <AmbiguousCard key={item.row_index} $unresolved={!resolved && !skipped}>
                      <AmbiguousQuery>
                        <strong>"{item.original_title}"</strong>
                        <MatchedMeta> — {'★'.repeat(item.rating)}</MatchedMeta>
                        {skipped && <MatchedMeta style={{ color: '#e53e3e' }}> (skipped)</MatchedMeta>}
                      </AmbiguousQuery>
                      <CandidateGrid>
                        {item.candidates.map(c => (
                          <CandidateCard
                            key={c.tmdb_id}
                            $selected={resolved?.tmdb_id === c.tmdb_id}
                            onClick={() => resolveAmbiguous(item.row_index, c)}
                          >
                            <strong>{c.title}</strong>
                            <span>
                              {releaseYear(c.release_date)}
                              {c.vote_average ? ` · ★${c.vote_average.toFixed(1)}` : ''}
                            </span>
                          </CandidateCard>
                        ))}
                      </CandidateGrid>
                      {!skipped && (
                        <SkipLink onClick={() => skipAmbiguous(item.row_index)}>
                          Skip this movie
                        </SkipLink>
                      )}
                      {skipped && (
                        <SkipLink onClick={() => { setSkippedAmbiguous(prev => { const n = new Set(prev); n.delete(item.row_index); return n; }); }}>
                          Undo skip
                        </SkipLink>
                      )}
                    </AmbiguousCard>
                  );
                })}
              </Section>
            )}

            {/* Not found */}
            {notFound.length > 0 && (
              <Section>
                <SectionTitle>Not found — search again with a different title, or skip</SectionTitle>
                {notFound.map(item => {
                  const rs = reSearchState.get(item.row_index);
                  const query = rs?.query ?? item.original_title;
                  return (
                    <NotFoundItem key={item.row_index}>
                      <span style={{ fontSize: '0.88rem', color: 'var(--color-text-secondary)' }}>
                        "{item.original_title}" — {'★'.repeat(item.rating)}
                      </span>
                      <ReSearchRow>
                        <TitleInput
                          value={query}
                          onChange={e => handleReSearchQueryChange(item.row_index, e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && handleReSearch(item)}
                          placeholder="Try a different title…"
                        />
                        <SmallBtn
                          onClick={() => handleReSearch(item)}
                          disabled={rs?.loading}
                        >
                          {rs?.loading ? 'Searching…' : 'Search again'}
                        </SmallBtn>
                      </ReSearchRow>
                      {rs?.noResult && (
                        <span style={{ fontSize: '0.82rem', color: '#e53e3e' }}>Still not found.</span>
                      )}
                      {rs?.candidates && rs.candidates.length > 0 && (
                        <ReSearchResults>
                          {rs.candidates.map(c => (
                            <ReSearchResultItem
                              key={c.tmdb_id}
                              onClick={() => selectReSearchResult(item, c)}
                            >
                              {c.title}
                              {c.release_date && <span>({releaseYear(c.release_date)})</span>}
                              {c.vote_average && <span>★{c.vote_average.toFixed(1)}</span>}
                            </ReSearchResultItem>
                          ))}
                        </ReSearchResults>
                      )}
                    </NotFoundItem>
                  );
                })}
              </Section>
            )}

            <Row>
              <Btn onClick={onClose}>Cancel</Btn>
              <Spacer />
              <Btn $variant="primary" onClick={handleSave} disabled={importItems.length === 0}>
                Import {importItems.length} movie{importItems.length !== 1 ? 's' : ''}
              </Btn>
            </Row>
          </>
        )}

        {/* ── Saving ── */}
        {step === 'saving' && (
          <Spinner>Saving ratings…</Spinner>
        )}

        {/* ── Done ── */}
        {step === 'done' && doneResult && (
          <>
            <ModalTitle>Import complete</ModalTitle>
            <Row style={{ justifyContent: 'center', gap: '3rem' }}>
              <DoneBox>
                <DoneStat>{doneResult.saved}</DoneStat>
                <DoneLabel>Saved</DoneLabel>
              </DoneBox>
              {doneResult.skipped > 0 && (
                <DoneBox>
                  <DoneStat style={{ color: 'var(--color-text-secondary)' }}>{doneResult.skipped}</DoneStat>
                  <DoneLabel>Already rated (skipped)</DoneLabel>
                </DoneBox>
              )}
            </Row>
            {doneResult.errors.length > 0 && (
              <ErrorMsg>{doneResult.errors.length} movie{doneResult.errors.length !== 1 ? 's' : ''} could not be saved.</ErrorMsg>
            )}
            <Row>
              <Spacer />
              <Btn $variant="primary" onClick={handleDone}>Close</Btn>
            </Row>
          </>
        )}

      </Modal>
    </Overlay>
  );
};

export default BulkImportModal;
